import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { billingApi } from "@/api/billing";
import { useSession } from "@/context/SessionContext";

// STAGE 6 · PHASE 5 — Stripe return handler. Polls the session status until it
// turns paid, then refreshes the wallet/tier and routes home.

export default function PaymentReturn({ mode }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshWallet, refreshUser } = useSession();
  const [state, setState] = useState(mode === "cancel" ? "cancel" : "polling");
  const [plan, setPlan] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (mode === "cancel") return;
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let live = true;
    const poll = async () => {
      try {
        const d = await billingApi.checkoutStatus(sid);
        setPlan(d.lookup_key);
        if (d.payment_status === "paid") {
          setState("paid");
          refreshWallet?.(); refreshUser?.();
          return;
        }
      } catch { /* keep trying */ }
      tries.current += 1;
      if (live && tries.current < 8) setTimeout(poll, 1600);
      else if (live) setState("pending");
    };
    poll();
    return () => { live = false; };
  }, [mode, params, refreshWallet, refreshUser]);

  const Icon = state === "paid" ? CheckCircle2 : (state === "cancel" || state === "error") ? XCircle : Loader2;
  const color = state === "paid" ? "#22C55E" : (state === "cancel" || state === "error") ? "#F87171" : "#D4AF37";
  const title = {
    polling: "Confirming payment…", paid: "Payment successful", pending: "Still processing",
    cancel: "Checkout canceled", error: "Something went wrong",
  }[state];
  const sub = {
    polling: "Hang tight while we verify your sandbox transaction.",
    paid: plan?.startsWith("coins") ? "Credits added to your wallet." : "Your subscription is now active.",
    pending: "Your payment is taking a moment — it'll reflect shortly.",
    cancel: "No charge was made. You can pick a plan any time.",
    error: "We couldn't read this checkout session.",
  }[state];

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-slate-950 p-8 text-center" data-testid="payment-return">
      <Icon size={56} style={{ color }} className={state === "polling" ? "animate-spin" : ""} />
      <h1 className="font-serif text-2xl text-white" data-testid="payment-return-title">{title}</h1>
      <p className="max-w-[260px] font-mono text-[11px] leading-relaxed text-white/50">{sub}</p>
      <button onClick={() => navigate("/")} data-testid="payment-return-home"
        className="mt-2 rounded-full border-2 border-white/20 bg-white/[0.06] px-6 py-3 font-mono text-[11px] font-black uppercase tracking-[0.18em] text-white active:scale-95">
        Back to Misteck
      </button>
    </div>
  );
}
